(ns ui.view.transaction-process.modals.decision.sub
  (:require
   [common.entity :as entity]
   [common.transaction-process.dictionary :as tp-dictionary]
   [common.transaction-process.permissions :as tp-permissions]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [reagent.ratom :as ratom]
   [ui.state.named-values :as named-values]
   [ui.view.transaction-process.modals.common :as common]
   [ui.view.transaction-process.sub :as tp-sub]
   [ui.view.transaction-process.util :as tp-util]))

(rf/reg-sub
 ::form
 (fn [db [_ {:keys [state-path] :as _ctx}]]
   (get-in db (common/form-full-path state-path))))

(defn- form-signal [[_ ctx]]
  [(rf/subscribe [::form ctx])])

(rf/reg-sub
 ::edit-mode?
 form-signal
 (fn [[f]]
   (:edit-mode? f)))

(rf/reg-sub
 ::submitting?
 form-signal
 (fn [[f]]
   (:submitting? f)))

(rf/reg-sub
 ::error
 form-signal
 (fn [[f]]
   (:error f)))

(rf/reg-sub
 ::decision-id
 form-signal
 (fn [[f]]
   (:decision-id f)))

(rf/reg-sub
 ::slot-kw
 form-signal
 (fn [[f]]
   (:slot-kw f)))

(rf/reg-sub
 ::decision-type-label
 form-signal
 (fn [[f]]
   (:decision-type-label f)))

(rf/reg-sub
 ::original-decision
 form-signal
 (fn [[f]]
   (get (:original-entities f) (:decision-id f))))

;; The slot's extra approval permission field, or nil when approving this slot
;; needs nothing beyond the ordinary decision write permission.
(rf/reg-sub
 ::approval-permission-field
 (fn [[_ ctx]]
   [(rf/subscribe [::tp-sub/template ctx])
    (rf/subscribe [::slot-kw ctx])])
 (fn [[template slot-kw]]
   (-> (get-in template [:template/dictionary slot-kw])
       tp-dictionary/approval-permission-field)))

;; True when the user may move this decision into or out of its ready state.
;; Slots with no approval field are approvable by anyone who can write them.
(rf/reg-sub-raw
 ::can-approve?
 (fn [_ [_ ctx]]
   (let [field-sub (rf/subscribe [::approval-permission-field ctx])]
     (ratom/reaction
      (if-let [field-kw @field-sub]
        @(rf/subscribe [::tp-sub/can-write-field? ctx field-kw])
        true)))))

;; An already-ready decision is read-only to a user who cannot approve: both save
;; actions write `:decision/draft?`, so Save as draft would un-approve it.
(rf/reg-sub
 ::approval-locked?
 (fn [[_ ctx]]
   [(rf/subscribe [::original-decision ctx])
    (rf/subscribe [::can-approve? ctx])])
 (fn [[original can-approve?]]
   (boolean (and (some? original)
                 (not can-approve?)
                 (tp-permissions/decision-ready? original)))))

(rf/reg-sub
 ::entities
 form-signal
 (fn [[f]]
   (:entities f)))

(rf/reg-sub
 ::decision
 (fn [[_ ctx]]
   [(rf/subscribe [::entities ctx])
    (rf/subscribe [::decision-id ctx])])
 (fn [[entities decision-id]]
   (get entities decision-id)))

(rf/reg-sub
 ::sections
 (fn [[_ ctx]]
   [(rf/subscribe [::tp-sub/template ctx])
    (rf/subscribe [::slot-kw ctx])])
 (fn [[template slot-kw]]
   (get (:template/decision-sections template) slot-kw)))

(rf/reg-sub
 ::reservation-layout
 (fn [[_ ctx]]
   [(rf/subscribe [::tp-sub/template ctx])])
 (fn [[template]]
   (tp-util/reservation-layout template)))

(rf/reg-sub
 ::field-configs
 (fn [[_ ctx]]
   [(rf/subscribe [::tp-sub/template ctx])])
 (fn [[template]]
   (:template/fields template)))

(rf/reg-sub
 ::reservation-enabled-fields
 (fn [[_ ctx]]
   [(rf/subscribe [::available-reservation-types ctx])])
 (fn [[types]]
   (into {}
         (map (fn [t]
                [(:db/id t)
                 (some-> (:investment-reservation-type/enabled-fields t) set)])
              types))))

(rf/reg-sub
 ::ready-errors
 (fn [[_ ctx]]
   [(rf/subscribe [::decision ctx])
    (rf/subscribe [::participants ctx])
    (rf/subscribe [::conditions ctx])
    (rf/subscribe [::reservations ctx])
    (rf/subscribe [::field-configs ctx])
    (rf/subscribe [::reservation-enabled-fields ctx])
    (rf/subscribe [::tp-sub/process ctx])])
 (fn [[decision participants conditions reservations field-configs reservation-enabled-fields process]]
   (tp-util/decision-ready-errors
    {:decision                   decision
     :participants               (map second participants)
     :conditions                 (map second conditions)
     :reservations               (map second reservations)
     :field-configs              field-configs
     :reservation-enabled-fields reservation-enabled-fields
     :process                    process})))

(rf/reg-sub
 ::participants
 (fn [[_ ctx]]
   [(rf/subscribe [::entities ctx])
    (rf/subscribe [::decision ctx])])
 (fn [[entities decision]]
   (->> (:decision/participants decision)
        (map (fn [pid] [pid (get entities pid)]))
        (remove (fn [[_ p]] (:common/disabled? p)))
        (sort-by (fn [[_ p]] (or (:common/ord p) 0)))
        vec)))

(rf/reg-sub
 ::existing-participant-person-ids
 (fn [[_ ctx]]
   [(rf/subscribe [::participants ctx])])
 (fn [[participants]]
   (->> participants
        (map (fn [[_ p]]
               (let [ref (:decision-participant/person p)]
                 (or (:db/id ref) ref))))
        (remove nil?)
        set)))

(rf/reg-sub
 ::conditions
 (fn [[_ ctx]]
   [(rf/subscribe [::entities ctx])
    (rf/subscribe [::decision ctx])])
 (fn [[entities decision]]
   (->> (:decision/conditions decision)
        (map (fn [cid] [cid (get entities cid)]))
        (remove (fn [[_ c]] (:common/disabled? c)))
        (sort-by (fn [[_ c]] (or (:common/ord c) 0)))
        vec)))

(rf/reg-sub
 ::reservations
 (fn [[_ ctx]]
   [(rf/subscribe [::entities ctx])
    (rf/subscribe [::decision ctx])])
 (fn [[entities decision]]
   (->> (:decision/investment-reservations decision)
        (map (fn [rid] [rid (get entities rid)]))
        (remove (fn [[_ r]] (:common/disabled? r)))
        (sort-by (fn [[_ r]] (or (:common/ord r) 0)))
        vec)))

(rf/reg-sub-raw
 ::person-options
 (fn [_ [_ ctx]]
   (let [existing-ids-sub (rf/subscribe [::existing-participant-person-ids ctx])]
     (ratom/reaction
      (let [existing-ids @existing-ids-sub]
        (->> (concat @session/persons (session/active-accounts nil))
             (remove (fn [{:keys [db/id]}] (contains? existing-ids id)))
             (mapv (fn [e]
                     {:value (:db/id e)
                      :label (entity/display-string e)}))))))))

(rf/reg-sub-raw
 ::contact-lists?
 (fn [_ _]
   (ratom/reaction (session/contact-lists?))))

(rf/reg-sub-raw
 ::contact-list-options
 (fn [_ _]
   (ratom/reaction
    (->> @session/contact-lists
         (mapv (fn [cl]
                 {:value (:db/id cl)
                  :label (entity/display-string cl)}))))))

(rf/reg-sub
 ::participant-role-options
 (fn [_ _]
   (rf/subscribe [::named-values/options :decision-participant/role]))
 (fn [options _]
   options))

(rf/reg-sub-raw
 ::available-reservation-types
 (fn [_ _]
   (ratom/reaction
    (->> @session/investment-reservation-types
         (remove :common/disabled?)
         (remove :common/archived?)
         (sort-by :common/ord)
         vec))))
